import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ShieldCheck, Loader2, CheckCircle2, AlertTriangle, Search, QrCode, Plus, X } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { QrScannerDialog } from "@/components/QrScannerDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STRINGS = {
  it: {
    eyebrow: "Wineemotion",
    title: "Registrazione garanzia",
    subtitle: "Registra uno o più dispenser dello stesso negozio per attivare la garanzia dalla data effettiva di installazione.",
    serials_label: "Numeri seriali dispenser *",
    serial_placeholder: "Es. 010224000345",
    serials_hint: "Aggiungi un seriale per ogni dispenser installato nello stesso negozio.",
    verify: "Verifica",
    scan: "Scansiona",
    add_dispenser: "Aggiungi un altro dispenser",
    remove_row: "Rimuovi",
    dispenser_found: "Dispenser trovato",
    model: "Modello",
    order: "Ordine",
    already_registered_warning: "La garanzia di questo dispenser è già stata registrata. Puoi aggiornare comunque i tuoi contatti, ma la data di installazione non verrà modificata.",
    common_data_title: "Dati comuni a tutti i dispenser",
    name_label: "Nome negozio / Cliente *",
    name_placeholder: "Es. Bar Milano, Enoteca La Vigna...",
    address_label: "Indirizzo di installazione",
    address_placeholder: "Via, numero civico, città, CAP",
    email_label: "Email *",
    email_placeholder: "tuoindirizzo@esempio.com",
    phone_label: "Telefono",
    install_date_label: "Data di installazione *",
    install_date_hint: "Stessa data per tutti i dispenser. La garanzia parte da questa data.",
    submit_one: "Registra garanzia",
    submit_many: "Registra {n} dispenser",
    submitting: "Invio in corso...",
    submit_hint: "Riceverai una email di conferma con un link. Clicca il link per finalizzare la registrazione di tutti i dispenser.",
    check_email_title: "Controlla la tua email",
    check_email_desc1: "Abbiamo inviato un link di conferma a",
    check_email_desc2_singular: "Clicca il link nell'email per completare la registrazione della garanzia.",
    check_email_desc2_plural: "Clicca il link nell'email per completare la registrazione di tutti i {n} dispenser insieme.",
    check_email_spam: "Non trovi l'email? Controlla nello spam. Il link scade tra 30 giorni.",
    email_failed: "Attenzione: l'invio email non ha avuto successo. Contatta il rivenditore per completare la registrazione.",
    err_no_serials: "Aggiungi e verifica almeno un seriale",
    err_verify_all: "Verifica prima tutti i seriali inseriti (bottone Verifica su ogni riga)",
    err_name: "Nome negozio / cliente obbligatorio",
    err_email: "Email valida obbligatoria",
    err_date: "Data di installazione obbligatoria",
    err_date_future: "La data di installazione non può essere futura",
    err_generic: "Errore durante la registrazione",
    err_lookup: "Errore lookup",
    err_duplicate: "Seriale già inserito",
    footer: "© Wineemotion · Assistenza tecnica",
  },
  en: {
    eyebrow: "Wineemotion",
    title: "Warranty Registration",
    subtitle: "Register one or more dispensers from the same shop to activate the warranty from the actual installation date.",
    serials_label: "Dispenser serial numbers *",
    serial_placeholder: "e.g. 010224000345",
    serials_hint: "Add one serial per dispenser installed at the same shop.",
    verify: "Verify",
    scan: "Scan",
    add_dispenser: "Add another dispenser",
    remove_row: "Remove",
    dispenser_found: "Dispenser found",
    model: "Model",
    order: "Order",
    already_registered_warning: "This dispenser has already been registered. You can still update your contact info, but the installation date will not be changed.",
    common_data_title: "Shared data for all dispensers",
    name_label: "Shop name / Customer *",
    name_placeholder: "e.g. Milano Wine Bar, La Vigna Enoteca...",
    address_label: "Installation address",
    address_placeholder: "Street, city, ZIP, country",
    email_label: "Email *",
    email_placeholder: "your.address@example.com",
    phone_label: "Phone",
    install_date_label: "Installation date *",
    install_date_hint: "Same date for all dispensers. Warranty starts from this date.",
    submit_one: "Register warranty",
    submit_many: "Register {n} dispensers",
    submitting: "Submitting...",
    submit_hint: "You will receive a confirmation email with a link. Click the link to finalise the registration for all dispensers.",
    check_email_title: "Check your email",
    check_email_desc1: "We sent a confirmation link to",
    check_email_desc2_singular: "Click the link in the email to complete the warranty registration.",
    check_email_desc2_plural: "Click the link in the email to complete the registration for all {n} dispensers together.",
    check_email_spam: "Can't find the email? Check your spam folder. The link expires in 30 days.",
    email_failed: "Warning: the email delivery failed. Please contact your reseller to complete the registration.",
    err_no_serials: "Please add and verify at least one serial",
    err_verify_all: "Please verify all serials first (Verify button on each row)",
    err_name: "Shop name / customer is required",
    err_email: "A valid email is required",
    err_date: "Installation date is required",
    err_date_future: "Installation date cannot be in the future",
    err_generic: "Error during registration",
    err_lookup: "Lookup error",
    err_duplicate: "Serial already added",
    footer: "© Wineemotion · Technical support",
  },
};

function useLang() {
  const [lang, setLang] = useState(() => {
    const stored = localStorage.getItem("wm_public_lang");
    if (stored === "it" || stored === "en") return stored;
    const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
    return nav === "it" ? "it" : "en";
  });
  return [lang, (l) => { setLang(l); localStorage.setItem("wm_public_lang", l); }];
}

const newRow = (serial = "") => ({
  id: Math.random().toString(36).slice(2, 10),
  serial,
  preview: null,
  loading: false,
  error: "",
});

/**
 * Public warranty registration page — supports MULTIPLE dispenser serials
 * per shop (double opt-in via email confirms all serials in one click).
 * URL: /garanzia?serial=XXX (optional pre-fill of first row).
 */
export default function WarrantyRegister() {
  const [params] = useSearchParams();
  const initialSerial = (params.get("serial") || params.get("s") || "").trim();
  const [lang, setLang] = useLang();
  const t = STRINGS[lang];

  const [rows, setRows] = useState(() => [newRow(initialSerial)]);
  const [common, setCommon] = useState({ end_customer_name: "", address: "", email: "", phone: "", install_date: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [scan, setScan] = useState({ open: false, rowId: null });

  const updateRow = useCallback((id, patch) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const doLookup = useCallback(async (rowId, serial) => {
    const s = (serial || "").trim();
    if (!s) { updateRow(rowId, { preview: null, error: "" }); return; }
    // Duplicate check
    const duplicate = rows.some((r) => r.id !== rowId && r.serial.trim() === s);
    if (duplicate) { updateRow(rowId, { preview: null, error: t.err_duplicate }); return; }
    updateRow(rowId, { loading: true, error: "" });
    try {
      const { data } = await axios.get(`${API}/public/warranty/lookup/${encodeURIComponent(s)}`);
      updateRow(rowId, { preview: data, loading: false, error: "" });
    } catch (e) {
      updateRow(rowId, { preview: null, loading: false, error: e.response?.data?.detail || t.err_lookup });
    }
  }, [rows, t.err_duplicate, t.err_lookup, updateRow]);

  // Auto-lookup pre-filled first serial
  useEffect(() => {
    if (initialSerial && rows.length === 1 && !rows[0].preview && !rows[0].loading) {
      doLookup(rows[0].id, initialSerial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSerial]);

  const addRow = () => setRows((rs) => [...rs, newRow()]);
  const removeRow = (id) => setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.id !== id) : rs));

  const anyVerified = rows.some((r) => !!r.preview);
  const allVerified = rows.every((r) => !!r.preview);
  const verifiedCount = rows.filter((r) => !!r.preview).length;

  const submit = async () => {
    if (!anyVerified) { toast.error(t.err_no_serials); return; }
    if (!allVerified) { toast.error(t.err_verify_all); return; }
    if (!common.end_customer_name.trim()) { toast.error(t.err_name); return; }
    if (!common.email.trim() || !common.email.includes("@")) { toast.error(t.err_email); return; }
    if (!common.install_date) { toast.error(t.err_date); return; }
    const today = new Date().toISOString().slice(0, 10);
    if (common.install_date > today) { toast.error(t.err_date_future); return; }

    setSubmitting(true);
    try {
      const payload = {
        dispenser_serials: rows.map((r) => r.serial.trim()).filter(Boolean),
        end_customer_name: common.end_customer_name.trim(),
        address: common.address,
        email: common.email.trim(),
        phone: common.phone,
        install_date: common.install_date,
        lang,
      };
      const { data } = await axios.post(`${API}/public/warranty/register`, payload);
      setSubmitted({ ...data, count: payload.dispenser_serials.length });
    } catch (e) {
      toast.error(e.response?.data?.detail || t.err_generic);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    const count = submitted.count || 1;
    const desc2 = count > 1
      ? t.check_email_desc2_plural.replace("{n}", count)
      : t.check_email_desc2_singular;
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4 py-10">
        <Card className="max-w-lg w-full p-8 rounded-sm bg-white border-zinc-200" data-testid="warranty-submitted">
          <div className="flex justify-center mb-4">
            <div className="h-14 w-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <CheckCircle2 className="h-7 w-7" strokeWidth={1.75} />
            </div>
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-900 text-center mb-3">{t.check_email_title}</h1>
          <p className="text-sm text-zinc-600 text-center leading-relaxed">
            {t.check_email_desc1} <b>{common.email}</b>. {desc2}
          </p>
          <p className="text-xs text-zinc-500 text-center mt-4">{t.check_email_spam}</p>
          {!submitted.email_sent && (
            <div className="mt-4 rounded-sm border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{t.email_failed}</span>
            </div>
          )}
        </Card>
      </div>
    );
  }

  const submitLabel = verifiedCount > 1 ? t.submit_many.replace("{n}", verifiedCount) : t.submit_one;

  return (
    <div className="min-h-screen bg-zinc-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        <div className="flex justify-center mb-6"><BrandLogo /></div>

        <div className="flex justify-end mb-4" data-testid="public-lang-switcher">
          <div className="inline-flex rounded-sm border border-zinc-200 bg-white text-[10px] uppercase tracking-widest">
            <button type="button" data-testid="public-lang-it" onClick={() => setLang("it")}
              className={`px-3 py-1.5 ${lang === "it" ? "bg-brand text-white" : "text-zinc-600 hover:bg-zinc-50"}`}>🇮🇹 IT</button>
            <button type="button" data-testid="public-lang-en" onClick={() => setLang("en")}
              className={`px-3 py-1.5 ${lang === "en" ? "bg-brand text-white" : "text-zinc-600 hover:bg-zinc-50"}`}>🇬🇧 EN</button>
          </div>
        </div>

        <Card className="p-6 sm:p-8 rounded-sm bg-white border-zinc-200" data-testid="warranty-register-page">
          <div className="flex items-start gap-3 mb-6">
            <div className="h-10 w-10 rounded-sm bg-brand-muted text-brand flex items-center justify-center shrink-0">
              <ShieldCheck className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t.eyebrow}</div>
              <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-900">{t.title}</h1>
              <p className="text-sm text-zinc-600 mt-1">{t.subtitle}</p>
            </div>
          </div>

          {/* SERIALS LIST */}
          <div className="space-y-4 mb-5">
            <Label className="text-xs uppercase tracking-widest text-zinc-500">{t.serials_label}</Label>
            {rows.map((row, idx) => (
              <div key={row.id} className="rounded-sm border border-zinc-200 p-3 bg-zinc-50/50" data-testid={`warranty-serial-row-${idx}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">#{idx + 1}</div>
                  {rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRow(row.id)}
                      data-testid={`warranty-serial-remove-${idx}`}
                      className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-red-700 hover:text-red-900"
                      title={t.remove_row}
                    >
                      <X className="h-3 w-3" /> {t.remove_row}
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    data-testid={`warranty-serial-${idx}`}
                    value={row.serial}
                    onChange={(e) => updateRow(row.id, { serial: e.target.value, preview: null, error: "" })}
                    placeholder={t.serial_placeholder}
                    className="rounded-sm font-mono bg-white"
                  />
                  <Button
                    data-testid={`warranty-scan-btn-${idx}`}
                    type="button"
                    variant="outline"
                    onClick={() => setScan({ open: true, rowId: row.id })}
                    className="rounded-sm shrink-0 px-3 bg-white"
                    title={t.scan}
                  >
                    <QrCode className="h-4 w-4" />
                    <span className="ml-1 hidden sm:inline">{t.scan}</span>
                  </Button>
                  <Button
                    data-testid={`warranty-lookup-btn-${idx}`}
                    type="button"
                    variant="outline"
                    onClick={() => doLookup(row.id, row.serial)}
                    disabled={row.loading || !row.serial.trim()}
                    className="rounded-sm shrink-0 bg-white"
                  >
                    {row.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-3.5 w-3.5 mr-1" /> {t.verify}</>}
                  </Button>
                </div>

                {row.error && (
                  <div data-testid={`warranty-serial-error-${idx}`} className="mt-2 rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 flex items-start gap-2">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>{row.error}</span>
                  </div>
                )}

                {row.preview && (
                  <div data-testid={`warranty-preview-${idx}`} className="mt-2 rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{t.dispenser_found}</div>
                        <div className="mt-0.5"><b>{t.model}:</b> {row.preview.model || "—"}</div>
                        {row.preview.order_note && <div><b>{t.order}:</b> {row.preview.order_note}</div>}
                        {row.preview.install_date_confirmed && (
                          <div className="mt-1 text-amber-900">
                            <AlertTriangle className="h-3 w-3 inline mr-1" />
                            {t.already_registered_warning}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            <div className="flex items-center justify-between">
              <button
                type="button"
                data-testid="warranty-add-row"
                onClick={addRow}
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-brand hover:underline"
              >
                <Plus className="h-3.5 w-3.5" /> {t.add_dispenser}
              </button>
              <div className="text-[10px] uppercase tracking-widest text-zinc-400">{verifiedCount}/{rows.length}</div>
            </div>
            <div className="text-[11px] text-zinc-500">{t.serials_hint}</div>
          </div>

          {/* COMMON DATA — visible only after at least one serial is verified */}
          {anyVerified && (
            <div className="space-y-3 pt-4 border-t border-zinc-100">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{t.common_data_title}</div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">{t.name_label}</Label>
                <Input data-testid="warranty-name" value={common.end_customer_name}
                  onChange={(e) => setCommon({ ...common, end_customer_name: e.target.value })}
                  className="mt-1 rounded-sm" placeholder={t.name_placeholder} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">{t.address_label}</Label>
                <Textarea data-testid="warranty-address" rows={2} value={common.address}
                  onChange={(e) => setCommon({ ...common, address: e.target.value })}
                  className="mt-1 rounded-sm" placeholder={t.address_placeholder} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">{t.email_label}</Label>
                  <Input data-testid="warranty-email" type="email" value={common.email}
                    onChange={(e) => setCommon({ ...common, email: e.target.value })}
                    className="mt-1 rounded-sm" placeholder={t.email_placeholder} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">{t.phone_label}</Label>
                  <Input data-testid="warranty-phone" type="tel" value={common.phone}
                    onChange={(e) => setCommon({ ...common, phone: e.target.value })}
                    className="mt-1 rounded-sm" placeholder="+39 ..." />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-500">{t.install_date_label}</Label>
                <Input data-testid="warranty-install-date" type="date" value={common.install_date}
                  onChange={(e) => setCommon({ ...common, install_date: e.target.value })}
                  max={new Date().toISOString().slice(0, 10)}
                  className="mt-1 rounded-sm" />
                <div className="mt-1 text-[11px] text-zinc-500">{t.install_date_hint}</div>
              </div>

              <div className="pt-3">
                <Button data-testid="warranty-submit" onClick={submit} disabled={submitting || !allVerified}
                  className="w-full bg-brand hover:bg-brand-dark text-white rounded-sm h-11">
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t.submitting}</> : submitLabel}
                </Button>
                <p className="text-[11px] text-zinc-500 text-center mt-3 leading-relaxed">{t.submit_hint}</p>
              </div>
            </div>
          )}
        </Card>

        <p className="text-center text-xs text-zinc-500 mt-6">{t.footer}</p>
      </div>

      <QrScannerDialog
        open={scan.open}
        onOpenChange={(v) => setScan((s) => ({ ...s, open: v }))}
        onSerialDetected={(serial) => {
          if (scan.rowId) {
            updateRow(scan.rowId, { serial, preview: null, error: "" });
            doLookup(scan.rowId, serial);
          }
        }}
      />
    </div>
  );
}
